import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  Avatar,
  Grid,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Add as AddIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { directoryApi, usersApi, locationsApi } from '../services/api';
import { EmployeeDirectoryItem, UserUpdate, UserCreate, DirectoryContactCreate, ConvertToUserRequest } from '../types';
import { useAuth } from '../context/AuthContext';

const Directory: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [jobTitleFilter, setJobTitleFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeDirectoryItem | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState<UserUpdate>({});
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createFormData, setCreateFormData] = useState<UserCreate>({
    email: '',
    username: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    employee_id: '',
    department: '',
    role: 'user',
  });
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactFormData, setContactFormData] = useState<DirectoryContactCreate>({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    employee_id: '',
    department: '',
    job_title: '',
    office_location: '',
  });
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertFormData, setConvertFormData] = useState<ConvertToUserRequest>({
    username: '',
    password: '',
    role: 'user',
  });
  const [error, setError] = useState<string | null>(null);

  const isManager = user?.role === 'manager';

  // Fetch directory
  const { data: directoryData, isLoading, refetch } = useQuery(
    ['directory', page, rowsPerPage, searchTerm, departmentFilter, locationFilter, jobTitleFilter, activeFilter],
    () =>
      directoryApi.getDirectory({
        skip: page * rowsPerPage,
        limit: rowsPerPage,
        search: searchTerm || undefined,
        department: departmentFilter !== 'all' ? departmentFilter : undefined,
        office_location: locationFilter !== 'all' ? locationFilter : undefined,
        job_title: jobTitleFilter !== 'all' ? jobTitleFilter : undefined,
        is_active: activeFilter === 'all' ? undefined : activeFilter === 'active',
      }),
    {
      onError: (err: any) => {
        setError(err?.response?.data?.detail || 'Failed to load directory');
      },
    }
  );

  // Fetch filters
  const { data: filtersData } = useQuery('directoryFilters', directoryApi.getFilters);

  // Fetch locations
  const { data: locationsData } = useQuery(['locations'], () =>
    locationsApi.getLocations({ is_active: true }));
  const locationOptions = locationsData?.locations.map((l) => l.name) || [];

  // Update mutation
  const updateMutation = useMutation(
    ({ userId, data }: { userId: number; data: UserUpdate }) =>
      directoryApi.updateEmployee(userId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['directory']);
        setEditDialogOpen(false);
        setSelectedEmployee(null);
      },
      onError: (err: any) => {
        setError(err?.response?.data?.detail || 'Failed to update employee');
      },
    }
  );

  // Deactivate mutation
  const deactivateMutation = useMutation(directoryApi.deactivateEmployee, {
    onSuccess: () => {
      queryClient.invalidateQueries(['directory']);
      setDetailDialogOpen(false);
      setSelectedEmployee(null);
    },
    onError: (err: any) => {
      setError(err?.response?.data?.detail || 'Failed to deactivate employee');
    },
  });

  // Create mutation (for system users)
  const createMutation = useMutation(usersApi.createUser, {
    onSuccess: () => {
      queryClient.invalidateQueries(['directory']);
      setCreateDialogOpen(false);
      setCreateFormData({
        email: '',
        username: '',
        password: '',
        first_name: '',
        last_name: '',
        phone: '',
        employee_id: '',
        department: '',
        role: 'user',
      });
    },
    onError: (err: any) => {
      setError(err?.response?.data?.detail || 'Failed to create employee');
    },
  });

  // Create directory contact mutation (no login credentials)
  const createContactMutation = useMutation(directoryApi.createDirectoryContact, {
    onSuccess: () => {
      queryClient.invalidateQueries(['directory']);
      setContactDialogOpen(false);
      setContactFormData({
        email: '',
        first_name: '',
        last_name: '',
        phone: '',
        employee_id: '',
        department: '',
        job_title: '',
        office_location: '',
      });
    },
    onError: (err: any) => {
      setError(err?.response?.data?.detail || 'Failed to create contact');
    },
  });

  // Convert to user mutation
  const convertToUserMutation = useMutation(
    ({ userId, data }: { userId: number; data: ConvertToUserRequest }) =>
      directoryApi.convertToUser(userId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['directory']);
        setConvertDialogOpen(false);
        setDetailDialogOpen(false);
        setSelectedEmployee(null);
        setConvertFormData({
          username: '',
          password: '',
          role: 'user',
        });
      },
      onError: (err: any) => {
        setError(err?.response?.data?.detail || 'Failed to convert to system user');
      },
    }
  );

  // Helper function to format phone for tel: link
  const formatPhoneForTel = (phone: string | undefined): string => {
    if (!phone) return '';
    const cleaned = phone.replace(/[^\d+]/g, '');
    return cleaned.startsWith('+') ? cleaned : `+1${cleaned}`;
  };

  // Handlers
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewEmployee = async (employeeId: number) => {
    try {
      const employee = await directoryApi.getEmployee(employeeId);
      setSelectedEmployee(employee);
      setDetailDialogOpen(true);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load employee details');
    }
  };

  const handleEditEmployee = (employee: EmployeeDirectoryItem) => {
    setSelectedEmployee(employee);
    setEditFormData({
      first_name: employee.first_name,
      last_name: employee.last_name,
      email: employee.email,
      phone: employee.phone,
      employee_id: employee.employee_id,
      department: employee.department,
      job_title: employee.job_title,
      office_location: employee.office_location,
      start_date: employee.start_date,
      manager_id: employee.manager_id,
      is_visible_in_directory: employee.is_visible_in_directory,
      is_active: employee.is_active,
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    if (selectedEmployee) {
      updateMutation.mutate({ userId: selectedEmployee.id, data: editFormData });
    }
  };

  const handleCreateEmployee = () => {
    createMutation.mutate(createFormData);
  };

  const handleCreateContact = () => {
    createContactMutation.mutate(contactFormData);
  };

  const handleDeactivate = () => {
    if (selectedEmployee && window.confirm(`Are you sure you want to deactivate ${selectedEmployee.first_name} ${selectedEmployee.last_name}?`)) {
      deactivateMutation.mutate(selectedEmployee.id);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setDepartmentFilter('all');
    setLocationFilter('all');
    setJobTitleFilter('all');
    setActiveFilter('active');
    setPage(0);
  };

  const handleOpenConvertDialog = () => {
    if (selectedEmployee) {
      // Pre-fill username with email prefix (before @)
      const suggestedUsername = selectedEmployee.email.split('@')[0].toLowerCase();
      setConvertFormData({
        username: suggestedUsername,
        password: '',
        role: 'user',
      });
      setConvertDialogOpen(true);
    }
  };

  const handleConvertToUser = () => {
    if (selectedEmployee) {
      convertToUserMutation.mutate({ userId: selectedEmployee.id, data: convertFormData });
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">
          Employee Directory
        </Typography>
        {isManager && (
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setContactDialogOpen(true)}
            >
              Add Contact
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Add User
            </Button>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              placeholder="Name, email, phone, department..."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Department</InputLabel>
              <Select
                value={departmentFilter}
                label="Department"
                onChange={(e) => setDepartmentFilter(e.target.value)}
              >
                <MenuItem value="all">All Departments</MenuItem>
                {filtersData?.departments.map((dept) => (
                  <MenuItem key={dept} value={dept}>
                    {dept}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Location</InputLabel>
              <Select
                value={locationFilter}
                label="Location"
                onChange={(e) => setLocationFilter(e.target.value)}
              >
                <MenuItem value="all">All Locations</MenuItem>
                {filtersData?.office_locations.map((loc) => (
                  <MenuItem key={loc} value={loc}>
                    {loc}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Job Title</InputLabel>
              <Select
                value={jobTitleFilter}
                label="Job Title"
                onChange={(e) => setJobTitleFilter(e.target.value)}
              >
                <MenuItem value="all">All Job Titles</MenuItem>
                {filtersData?.job_titles.map((title) => (
                  <MenuItem key={title} value={title}>
                    {title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {isManager && (
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={activeFilter}
                  label="Status"
                  onChange={(e) => setActiveFilter(e.target.value as any)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleClearFilters}
            startIcon={<RefreshIcon />}
          >
            Clear Filters
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => refetch()}
            startIcon={<RefreshIcon />}
          >
            Refresh
          </Button>
        </Box>
      </Paper>

      {/* Directory Table */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Job Title</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Email</TableCell>
                  {isManager && <TableCell>Status</TableCell>}
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {directoryData?.employees.map((employee) => (
                  <TableRow key={employee.id} hover>
                    <TableCell>
                      <Avatar src={employee.avatar_url}>
                        {employee.first_name[0]}{employee.last_name[0]}
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      {employee.first_name} {employee.last_name}
                      {employee.employee_id && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          ID: {employee.employee_id}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{employee.job_title || '-'}</TableCell>
                    <TableCell>{employee.department || '-'}</TableCell>
                    <TableCell>
                      {employee.phone ? (
                        <Link
                          href={`tel:${formatPhoneForTel(employee.phone)}`}
                          onClick={(e) => e.stopPropagation()}
                          underline="hover"
                        >
                          {employee.phone}
                        </Link>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <Link
                        href={`mailto:${employee.email}`}
                        onClick={(e) => e.stopPropagation()}
                        underline="hover"
                      >
                        {employee.email}
                      </Link>
                    </TableCell>
                    {isManager && (
                      <TableCell>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          <Chip
                            label={employee.is_active ? 'Active' : 'Inactive'}
                            color={employee.is_active ? 'success' : 'error'}
                            size="small"
                          />
                          {employee.is_directory_only && (
                            <Chip
                              label="Directory Only"
                              color="warning"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </TableCell>
                    )}
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleViewEmployee(employee.id)}
                        title="View Details"
                      >
                        <VisibilityIcon />
                      </IconButton>
                      {isManager && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handleEditEmployee(employee)}
                            title="Edit"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setSelectedEmployee(employee);
                              handleDeactivate();
                            }}
                            title="Deactivate"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {directoryData?.employees.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={isManager ? 8 : 7} align="center">
                      No employees found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={directoryData?.total || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </>
      )}

      {/* Employee Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        {selectedEmployee && (
          <>
            <DialogTitle>
              {selectedEmployee.first_name} {selectedEmployee.last_name}
            </DialogTitle>
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={2} mt={1}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar
                    src={selectedEmployee.avatar_url}
                    sx={{ width: 80, height: 80 }}
                  >
                    <PersonIcon sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6">
                      {selectedEmployee.first_name} {selectedEmployee.last_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedEmployee.job_title || 'No job title'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedEmployee.department || 'No department'}
                    </Typography>
                    {isManager && selectedEmployee.is_directory_only && (
                      <Chip
                        label="Directory Only - No System Access"
                        color="warning"
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Box>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <EmailIcon color="action" />
                  <Link href={`mailto:${selectedEmployee.email}`} underline="hover">
                    {selectedEmployee.email}
                  </Link>
                </Box>

                {selectedEmployee.phone && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <PhoneIcon color="action" />
                    <Link
                      href={`tel:${formatPhoneForTel(selectedEmployee.phone)}`}
                      underline="hover"
                    >
                      {selectedEmployee.phone}
                    </Link>
                  </Box>
                )}

                {selectedEmployee.office_location && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Office Location
                    </Typography>
                    <Typography variant="body1">{selectedEmployee.office_location}</Typography>
                  </Box>
                )}

                {selectedEmployee.start_date && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Start Date
                    </Typography>
                    <Typography variant="body1">
                      {new Date(selectedEmployee.start_date).toLocaleDateString('en-US', { timeZone: 'America/New_York' })}
                    </Typography>
                  </Box>
                )}

                {selectedEmployee.manager_name && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Reports to
                    </Typography>
                    <Typography variant="body1">{selectedEmployee.manager_name}</Typography>
                  </Box>
                )}

                {selectedEmployee.employee_id && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Employee ID
                    </Typography>
                    <Typography variant="body1">{selectedEmployee.employee_id}</Typography>
                  </Box>
                )}

                <Box display="flex" gap={1} mt={2}>
                  <Button
                    variant="outlined"
                    startIcon={<EmailIcon />}
                    href={`mailto:${selectedEmployee.email}`}
                    component="a"
                  >
                    Email
                  </Button>
                  {selectedEmployee.phone && (
                    <Button
                      variant="outlined"
                      startIcon={<PhoneIcon />}
                      href={`tel:${formatPhoneForTel(selectedEmployee.phone)}`}
                      component="a"
                    >
                      Call
                    </Button>
                  )}
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              {isManager && selectedEmployee.is_directory_only && (
                <Button
                  onClick={handleOpenConvertDialog}
                  startIcon={<PersonAddIcon />}
                  color="primary"
                  variant="contained"
                >
                  Grant System Access
                </Button>
              )}
              {isManager && (
                <Button onClick={() => handleEditEmployee(selectedEmployee)}>
                  Edit
                </Button>
              )}
              <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Edit Employee Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Employee</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="First Name"
              value={editFormData.first_name || ''}
              onChange={(e) => setEditFormData({ ...editFormData, first_name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Last Name"
              value={editFormData.last_name || ''}
              onChange={(e) => setEditFormData({ ...editFormData, last_name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={editFormData.email || ''}
              onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
              fullWidth
            />
            <TextField
              label="Phone"
              value={editFormData.phone || ''}
              onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
              fullWidth
            />
            <TextField
              label="Employee ID"
              value={editFormData.employee_id || ''}
              onChange={(e) => setEditFormData({ ...editFormData, employee_id: e.target.value })}
              fullWidth
            />
            <TextField
              label="Department"
              value={editFormData.department || ''}
              onChange={(e) => setEditFormData({ ...editFormData, department: e.target.value })}
              fullWidth
            />
            <TextField
              label="Job Title"
              value={editFormData.job_title || ''}
              onChange={(e) => setEditFormData({ ...editFormData, job_title: e.target.value })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Office Location</InputLabel>
              <Select
                value={editFormData.office_location || ''}
                label="Office Location"
                onChange={(e) => setEditFormData({ ...editFormData, office_location: e.target.value })}
              >
                <MenuItem value="">None</MenuItem>
                {(editFormData.office_location && !locationOptions.includes(editFormData.office_location)
                  ? [editFormData.office_location, ...locationOptions]
                  : locationOptions
                ).map((loc) => (
                  <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editFormData.is_active ? 'active' : 'inactive'}
                label="Status"
                onChange={(e) =>
                  setEditFormData({ ...editFormData, is_active: e.target.value === 'active' })
                }
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={updateMutation.isLoading}
          >
            {updateMutation.isLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Employee Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Employee</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="First Name"
              value={createFormData.first_name}
              onChange={(e) => setCreateFormData({ ...createFormData, first_name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Last Name"
              value={createFormData.last_name}
              onChange={(e) => setCreateFormData({ ...createFormData, last_name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Username"
              value={createFormData.username}
              onChange={(e) => setCreateFormData({ ...createFormData, username: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              value={createFormData.email}
              onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Password"
              type="password"
              value={createFormData.password}
              onChange={(e) => setCreateFormData({ ...createFormData, password: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Phone"
              value={createFormData.phone}
              onChange={(e) => setCreateFormData({ ...createFormData, phone: e.target.value })}
              fullWidth
            />
            <TextField
              label="Employee ID"
              value={createFormData.employee_id}
              onChange={(e) => setCreateFormData({ ...createFormData, employee_id: e.target.value })}
              fullWidth
            />
            <TextField
              label="Department"
              value={createFormData.department}
              onChange={(e) => setCreateFormData({ ...createFormData, department: e.target.value })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={createFormData.role}
                label="Role"
                onChange={(e) =>
                  setCreateFormData({ ...createFormData, role: e.target.value as 'user' | 'manager' })
                }
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateEmployee}
            variant="contained"
            disabled={createMutation.isLoading}
          >
            {createMutation.isLoading ? 'Creating...' : 'Create Employee'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Directory Contact Dialog (no login credentials) */}
      <Dialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Directory Contact</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add a contact to the directory without creating a system login account.
          </Typography>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="First Name"
              value={contactFormData.first_name}
              onChange={(e) => setContactFormData({ ...contactFormData, first_name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Last Name"
              value={contactFormData.last_name}
              onChange={(e) => setContactFormData({ ...contactFormData, last_name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              value={contactFormData.email}
              onChange={(e) => setContactFormData({ ...contactFormData, email: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Phone"
              value={contactFormData.phone}
              onChange={(e) => setContactFormData({ ...contactFormData, phone: e.target.value })}
              fullWidth
            />
            <TextField
              label="Employee ID"
              value={contactFormData.employee_id}
              onChange={(e) => setContactFormData({ ...contactFormData, employee_id: e.target.value })}
              fullWidth
            />
            <TextField
              label="Department"
              value={contactFormData.department}
              onChange={(e) => setContactFormData({ ...contactFormData, department: e.target.value })}
              fullWidth
            />
            <TextField
              label="Job Title"
              value={contactFormData.job_title}
              onChange={(e) => setContactFormData({ ...contactFormData, job_title: e.target.value })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Office Location</InputLabel>
              <Select
                value={contactFormData.office_location}
                label="Office Location"
                onChange={(e) => setContactFormData({ ...contactFormData, office_location: e.target.value })}
              >
                <MenuItem value="">None</MenuItem>
                {locationOptions.map((loc) => (
                  <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateContact}
            variant="contained"
            disabled={createContactMutation.isLoading}
          >
            {createContactMutation.isLoading ? 'Creating...' : 'Add Contact'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert to System User Dialog */}
      <Dialog
        open={convertDialogOpen}
        onClose={() => setConvertDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Grant System Access</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create login credentials for {selectedEmployee?.first_name} {selectedEmployee?.last_name} to access the internal system.
          </Typography>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Username"
              value={convertFormData.username}
              onChange={(e) => setConvertFormData({ ...convertFormData, username: e.target.value })}
              fullWidth
              required
              helperText="This will be used to log in to the system"
            />
            <TextField
              label="Password"
              type="password"
              value={convertFormData.password}
              onChange={(e) => setConvertFormData({ ...convertFormData, password: e.target.value })}
              fullWidth
              required
              helperText="Minimum 6 characters recommended"
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={convertFormData.role}
                label="Role"
                onChange={(e) =>
                  setConvertFormData({ ...convertFormData, role: e.target.value as 'user' | 'manager' })
                }
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConvertToUser}
            variant="contained"
            disabled={convertToUserMutation.isLoading || !convertFormData.username || !convertFormData.password}
          >
            {convertToUserMutation.isLoading ? 'Granting Access...' : 'Grant Access'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Directory;
